import { Injectable, OnDestroy, NgZone, inject } from '@angular/core';
import { CSS_VARIABLES_CONFIG } from '../config/css-variables.config';

export interface CSSVariableConfig {
  name: string;
  selector: string;
  property: string;
  fallback?: string;
  unit?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalCSSVariablesService implements OnDestroy {
  private ngZone = inject(NgZone);

  private observers: Map<string, ResizeObserver> = new Map();
  private mutationObserver?: MutationObserver;
  private isInitialized = false;

  // Konfiguration für CSS-Variablen aus der externen Konfigurationsdatei
  private readonly cssVariables: CSSVariableConfig[] = [...CSS_VARIABLES_CONFIG];

  constructor() {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  /**
   * Initialisiert den Service und startet die automatische Überwachung
   */
  private initialize(): void {
    if (this.isInitialized) return;
    
    // Warte bis das DOM geladen ist
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.startMonitoring());
    } else {
      this.startMonitoring();
    }
    
    this.isInitialized = true;
  }

  /**
   * Startet die Überwachung aller konfigurierten CSS-Variablen
   */
  private startMonitoring(): void {
    // Initiale Messung mit Verzögerung für besseres Timing nach DOM-Änderungen
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.ngZone.run(() => {
          this.measureAllVariables();
        });
      });
    });
    
    // Überwache DOM-Änderungen für neue Elemente
    this.observeDOMChanges();
    
    // Überwache Fenstergrößenänderungen
    window.addEventListener('resize', () => this.measureAllVariables());
  }

  /**
   * Misst alle konfigurierten CSS-Variablen
   */
  private measureAllVariables(): void {
    this.cssVariables.forEach(config => {
      this.measureCSSVariable(config);
    });
  }

  /**
   * Misst eine spezifische CSS-Variable
   */
  private measureCSSVariable(config: CSSVariableConfig, retryCount: number = 0): void {
    const elements = document.querySelectorAll(config.selector);
    
    if (elements.length > 0) {
      // Verwende das erste gefundene Element
      const element = elements[0] as HTMLElement;
      const value = this.getElementValue(element, config);
      
      if (value !== null) {
        // Prüfe aktuellen Wert vor Überschreibung
        const currentValue = this.getCurrentNumericValue(config.name);
        
        // Verhindere Überschreibung: Wenn gemessener Wert 0 ist, aber bereits ein Wert > 0 gesetzt ist, überschreibe nicht
        if (value === 0 && currentValue !== null && currentValue > 0) {
          // Element ist noch nicht gerendert, aber wir haben bereits einen gültigen Wert
          // Versuche Retry wenn noch Versuche übrig sind
          if (retryCount < 3) {
            this.ngZone.runOutsideAngular(() => {
              requestAnimationFrame(() => {
                this.ngZone.run(() => {
                  this.measureCSSVariable(config, retryCount + 1);
                });
              });
            });
          }
          // Behalte den aktuellen Wert bei
          return;
        }
        
        // Setze neuen Wert nur wenn er gültig ist (> 0) oder wenn kein Wert gesetzt ist
        if (value > 0 || currentValue === null) {
          this.setCSSVariable(config.name, value, config.unit);
          this.observeElement(element, config);
        } else if (value === 0 && retryCount < 3) {
          // Retry-Logik: Wenn Wert 0 ist und noch Versuche übrig sind
          this.ngZone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
              this.ngZone.run(() => {
                this.measureCSSVariable(config, retryCount + 1);
              });
            });
          });
        }
      }
    }
  }

  /**
   * Holt den Wert eines Elements basierend auf der Konfiguration
   */
  private getElementValue(element: HTMLElement, config: CSSVariableConfig): number | null {
    switch (config.property) {
      case 'offsetHeight':
        return element.offsetHeight;
      case 'offsetWidth':
        return element.offsetWidth;
      case 'clientHeight':
        return element.clientHeight;
      case 'clientWidth':
        return element.clientWidth;
      case 'scrollHeight':
        return element.scrollHeight;
      case 'scrollWidth':
        return element.scrollWidth;
      default:
        return null;
    }
  }

  /**
   * Setzt eine CSS-Variable im :root
   */
  private setCSSVariable(name: string, value: number, unit: string = 'px'): void {
    const cssValue = `${value}${unit}`;
    document.documentElement.style.setProperty(name, cssValue);
    // console.log(`CSS Variable gesetzt: ${name} = ${cssValue}`);
  }

  /**
   * Gibt den aktuellen numerischen Wert einer CSS-Variable zurück
   */
  private getCurrentNumericValue(name: string): number | null {
    const currentValue = this.getCSSVariableValue(name);
    if (!currentValue || currentValue === '0px' || currentValue === '0') {
      return null;
    }
    // Extrahiere numerischen Wert (entferne 'px', 'rem', etc.)
    const numericMatch = currentValue.match(/^([\d.]+)/);
    if (numericMatch) {
      return parseFloat(numericMatch[1]);
    }
    return null;
  }

  /**
   * Überwacht ein Element auf Größenänderungen
   */
  private observeElement(element: HTMLElement, config: CSSVariableConfig): void {
    const observerKey = `${config.selector}-${config.name}`;
    
    // Entferne existierenden Observer
    if (this.observers.has(observerKey)) {
      this.observers.get(observerKey)?.disconnect();
    }

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => {
        const value = this.getElementValue(element, config);
        if (value !== null && value > 0) {
          // Nur setzen wenn Wert > 0, um 0px-Überschreibung zu vermeiden
          const currentValue = this.getCurrentNumericValue(config.name);
          if (value > 0 || currentValue === null) {
            this.setCSSVariable(config.name, value, config.unit);
          }
        }
      });
      
      resizeObserver.observe(element);
      this.observers.set(observerKey, resizeObserver);
    }
  }

  /**
   * Überwacht DOM-Änderungen für neue Elemente
   */
  private observeDOMChanges(): void {
    if ('MutationObserver' in window) {
      this.mutationObserver = new MutationObserver((mutations) => {
        let shouldRemeasure = false;
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                // Prüfe ob ein neues Element hinzugefügt wurde, das wir überwachen
                this.cssVariables.forEach(config => {
                  if (element.matches && element.matches(config.selector)) {
                    shouldRemeasure = true;
                  }
                });
              }
            });
          }
        });
        
        if (shouldRemeasure) {
          // Use NgZone and requestAnimationFrame for better performance and to avoid memory leaks
          // Doppelte Verzögerung für besseres Timing nach DOM-Änderungen
          this.ngZone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.ngZone.run(() => {
                  this.measureAllVariables();
                });
              });
            });
          });
        }
      });
      
      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Fügt eine neue CSS-Variable zur Überwachung hinzu
   */
  addCSSVariable(config: CSSVariableConfig): void {
    this.cssVariables.push(config);
    this.measureCSSVariable(config);
  }

  /**
   * Entfernt eine CSS-Variable aus der Überwachung
   */
  removeCSSVariable(name: string): void {
    const index = this.cssVariables.findIndex(config => config.name === name);
    if (index > -1) {
      this.cssVariables.splice(index, 1);
      
      // Entferne Observer
      const observerKey = `${this.cssVariables[index]?.selector}-${name}`;
      if (this.observers.has(observerKey)) {
        this.observers.get(observerKey)?.disconnect();
        this.observers.delete(observerKey);
      }
      
      // Entferne CSS-Variable
      document.documentElement.style.removeProperty(name);
    }
  }

  /**
   * Gibt alle konfigurierten CSS-Variablen zurück
   */
  getConfiguredVariables(): CSSVariableConfig[] {
    return [...this.cssVariables];
  }

  /**
   * Gibt den aktuellen Wert einer CSS-Variable zurück
   */
  getCSSVariableValue(name: string): string | null {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  /**
   * Bereinigt alle Observer
   */
  private cleanup(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    
    window.removeEventListener('resize', () => this.measureAllVariables());
  }
}
